import DataGrid, {
  Column,
  ColumnChooser,
  ColumnChooserSearch,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Item,
  MasterDetail,
  Scrolling,
  SearchPanel,
  Sorting,
  StateStoring,
  Toolbar,
  DataGridTypes,
  DataGridRef,
  Search,
  Pager,
  Button as GridButton,
} from 'devextreme-react/data-grid'
import ODataStore from 'devextreme/data/odata/store'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { useScreenSize } from '@/themes/media-query'
import notify from 'devextreme/ui/notify'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import dxDataGrid, {
  CellHoverChangedEvent,
  CellPreparedEvent,
  ColumnButtonClickEvent,
  dxDataGridColumn,
  ExportingEvent,
  RowDblClickEvent,
} from 'devextreme/ui/data_grid'
import { Popover, PopoverRef } from 'devextreme-react/popover'
import { confirm } from 'devextreme/ui/dialog'
import { useLoaderData } from 'react-router-typesafe'
import { FormMode, GridCellColor } from '@/enums'
import { useNavigate, useParams } from 'react-router-dom'
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion'
import { ClickEvent } from 'devextreme/ui/button'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { SoftwareAltrePiattaformeGridLoader } from '@/routes/software/altre-piattaforme/SoftwareAltrePiattaformeGrid.route'
import { SoftwareAltrePiattaformeEditorForm } from '@/routes/software/altre-piattaforme/SoftwareAltrePiattaformeEditor.form'
import { ContentReadyEvent } from 'devextreme/ui/accordion'
import {
  createDateFilterItemDescriptor,
  FormFilterItemDescriptorType,
} from '@/components/filter-form/GenericFilterForm.types'
import GenericFilterForm from '@/components/filter-form/GenericFilterForm'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import { columnSourceFactory, isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { est_licenza } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { Roles } from '@/auth/azure/Roles'
import { gridTotalItemsLabel } from '@/constants'
import DocumentHead from '@/components/document-head/DocumentHead'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'

const cellPrepared = (e: CellPreparedEvent) => {
  if (e.rowType === 'data') {
    if (e.column.dataField === 'piano_mantenimento.nome') {
      if (e.value) {
        switch (e.value) {
          case 'SWITCH': {
            e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.ORANGE}`

            break
          }
          case 'ATTIVO': {
            e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.EMERALD}`

            break
          }
          case 'SCADUTO': {
            e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.SALMON}`

            break
          }
          case 'ALTRO DEALER': {
            e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.YELLOW}`

            break
          }
          // No default
        }
      }
    } else if (e.column.dataField === 'stato_noleggio.nome' && e.value) {
      switch (e.value) {
        case 'ALTRO DEALER': {
          e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.YELLOW}`

          break
        }
        case 'ATTIVO': {
          e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.EMERALD}`

          break
        }
        case 'SCADUTO': {
          e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.SALMON}`

          break
        }
        // No default
      }
    }
  }
}

const getColumnTooltip = (col: dxDataGridColumn): string => {
  switch (col.dataField) {
    case 'tradein': {
      return 'TRADE IN'
    }
    case 'rete': {
      return 'LICENZA DI RETE'
    }
    case 'n_contratto': {
      return 'NUMERO DI CONTRATTO'
    }
    case 'postazioni': {
      return 'POSTAZIONI'
    }
    case 'altro_fornitore': {
      return 'ALTRO FORNITORE'
    }
    case 'contratto_precedente': {
      return 'CONTRATTO PRECEDENTE'
    }
    // No default
  }
  return col.caption ?? ''
}

const onExporting = (e: ExportingEvent) => {
  ExportDataGridToExcel(e.component)
}

const ExportDataGridToExcel = (component: dxDataGrid) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet('ClientsGrid')
  exportDataGrid({ component, worksheet }).then(() => {
    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'AltrePiattaformeLicense.xlsx')
    })
  })
}

const SoftwareAltrePiattaformeGrid = () => {
  const currentScreenSize = useScreenSize()
  const { clientId } = useParams()
  const token = useTokenRefresh()
  const {
    title,
    altrePiattaformePlatforms,
    altrePiattaformeMaintenancePlans,
    altrePiattaformeRentStatus,
    aziendaList,
    userInfo,
    defaultCRUDAllowedRoles,
  } = useLoaderData<typeof SoftwareAltrePiattaformeGridLoader>()

  const { service } = useQsAdminApiManager()

  const navigate = useNavigate()
  const swAltrePiattaformeGridRef = useRef<DataGridRef>(null)
  const gridPopoverRef = useRef<PopoverRef>(null)

  const getFiltersConfiguration = useCallback((): FormFilterItemDescriptorType[] => {
    const fltConf: FormFilterItemDescriptorType[] = [
      {
        fieldName: 'azienda',
        placeHolder: 'Azienda',
        valueKeyName: 'id',
        valueDisplayExpr: 'nome',
        values: aziendaList.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['sede/azienda/id', this.currentValue]]
        },
      },
      createDateFilterItemDescriptor('data_vendita', 'Data Vendita'),
    ]
    console.log('GENFLT - GETCONF :', fltConf)
    return fltConf
  }, [aziendaList])

  const initialFilterConfiguration = useMemo(() => getFiltersConfiguration(), [getFiltersConfiguration])
  const [filterConfiguration, setFilterConfiguration] = useState(initialFilterConfiguration)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const getCurrentFilter = useCallback((): any[] => {
    let applied: boolean = false
    const currentFlts = []
    for (const flt of filterConfiguration) {
      const fltValue = flt.composeFilterItem()
      if (fltValue !== null) {
        currentFlts.push(fltValue)
      }
      if (!applied && flt.currentValue !== flt.defaultValue) applied = true
    }
    setFiltersApplied(applied)
    console.log('GENFLT - CURRENT:', currentFlts)
    return currentFlts
  }, [filterConfiguration])

  const [currentFilter, setCurrentFilter] = useState<any[]>(clientId ? [['sede/azienda/id', Number(clientId)]] : [])

  const swAltrePiattaformeDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/est_licenza`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.log(e.errorDetails)
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    filter: currentFilter.length > 0 ? currentFilter : null,
    expand: [
      'sede($expand=azienda($expand=agente($expand=commerciale_qs),rivenditore,sedi))',
      'software',
      'piano_mantenimento',
      'stato_noleggio',
    ],
  }

  const getGridHeight = useCallback(() => {
    let h = '72vh'
    if (currentScreenSize.isMedium) {
      h = '68vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '65vh'
    }
    return h
  }, [currentScreenSize])

  const onCellHoverChanged = (e: CellHoverChangedEvent) => {
    if (e.rowType === 'header' && e.eventType === 'mouseover') {
      gridPopoverRef.current?.instance().option('contentTemplate', function (contentElement: any) {
        const a: dxDataGridColumn = e.column
        return `<div><b>${getColumnTooltip(e.column)}</b></div>`
      })
      gridPopoverRef.current?.instance().option('target', e.cellElement)
      gridPopoverRef.current?.instance().show()
    }
    if (e.eventType === 'mouseout') {
      gridPopoverRef.current?.instance().hide()
    }
  }

  const deleteLicenza = async (licenseId: number) => {
    return await service.est_licenza(licenseId).delete()
  }

  useEffect(() => {
    getCurrentFilter()
  }, [])

  return (
    <>
      {!clientId && (
        <div className="accordion-generic-filter-form">
          <Accordion
            className={filtersApplied ? 'with-filter' : ''}
            collapsible={true}
            onContentReady={(e: ContentReadyEvent<any, any>) => {
              e.component?.collapseItem(0)
            }}
          >
            <AccordionItem icon="filter" title={'Filtri'}>
              <GenericFilterForm
                FormItemDescriptors={filterConfiguration}
                resetFilterClicked={function (e: ClickEvent): void {
                  setFilterConfiguration(getFiltersConfiguration())
                }}
                filterValueChanged={function (): void {
                  const currentFlts = getCurrentFilter()
                  console.log('GENFLT - VALUE CHNG', currentFlts)
                  if (JSON.stringify(currentFlts) !== JSON.stringify(currentFilter)) {
                    console.log('GENFLT - SETTING FILT', currentFlts)
                    setCurrentFilter(currentFlts)
                  }
                }}
              />
            </AccordionItem>
          </Accordion>
        </div>
      )}
      <DataGrid
        id={clientId ? 'client-sw-altrepiattaforme-grid' : 'sw-altrepiattaforme-grid'}
        height={getGridHeight()}
        className={'grid dx-card wide-card'}
        noDataText="Nessuna licenza trovata"
        dataSource={swAltrePiattaformeDataSource}
        showBorders={false}
        showColumnLines={true}
        focusedRowEnabled={true}
        defaultFocusedRowIndex={0}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
        allowColumnResizing={true}
        allowColumnReordering={true}
        rowAlternationEnabled={true}
        wordWrapEnabled={false}
        width="100%"
        onExporting={onExporting}
        onCellPrepared={cellPrepared}
        onCellHoverChanged={onCellHoverChanged}
        ref={swAltrePiattaformeGridRef}
        onRowDblClick={(e: RowDblClickEvent<any, any>) => {
          if (e.rowType === 'data') navigate(`${e.data.id}`)
        }}
        remoteOperations={{
          filtering: true,
          grouping: false,
          groupPaging: false,
          paging: true,
          sorting: true,
          summary: true,
        }}
        groupPanel={{
          visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
          emptyPanelText: 'Trascina qui una colonna per raggruppare',
        }}
      >
        <StateStoring
          enabled={true}
          type="localStorage"
          storageKey={clientId ? 'client-sw-altrepiattaforme-grid' : 'sw-altrepiattaforme-grid'}
          savingTimeout={50}
        />
        <Pager
          visible={true}
          allowedPageSizes={'auto'}
          displayMode={'compact'}
          showPageSizeSelector={false}
          showInfo={true}
          showNavigationButtons={false}
          infoText={gridTotalItemsLabel}
        />
        <Export enabled={true} />
        <HeaderFilter visible={true} />
        <GroupPanel visible={true} emptyPanelText="Trascina qui una colonna per raggruppare" />
        <Grouping autoExpandAll={true} />
        <FilterRow visible={true} />
        <FilterPanel visible={true} />
        <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 180 : 250} />
        <Scrolling mode="virtual" />
        <Sorting mode="multiple" />
        <ColumnChooser enabled={true} title={'Scelta Colonne'}>
          <ColumnChooserSearch enabled={true} />
        </ColumnChooser>
        <Editing allowAdding={true} />
        <MasterDetail
          enabled={true}
          render={(props: DataGridTypes.MasterDetailTemplateData) => (
            <SoftwareAltrePiattaformeEditorForm
              mode={FormMode.View}
              license={props.data}
              altrePiattaformeRentStatus={altrePiattaformeRentStatus.data.value}
              altrePiattaformeMaintenancePlans={altrePiattaformeMaintenancePlans.data.value}
              altrePiattaformePlatforms={altrePiattaformePlatforms.data.value}
              aziendaList={aziendaList.data.value}
            />
          )}
        />
        <Column
          visible={false}
          type="buttons"
          width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
          alignment="left"
          showInColumnChooser={false}
        >
          <GridButton
            hint="Details"
            icon="search"
            onClick={(e: ColumnButtonClickEvent) => {
              navigate(`${e.row?.data.id}`)
            }}
          ></GridButton>
        </Column>
        <Column
          dataField={'id'}
          caption="ID"
          visible={false}
          dataType="number"
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 14 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'id', true)}></HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'sede.azienda.nome'}
          cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
            const data: est_licenza = cellData.row.data
            return (
              <ObjectCodeLink
                url={`/clients/${data.sede?.azienda?.id}`}
                text={data.sede?.azienda?.nome}
                disabled={clientId ? true : !isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              ></ObjectCodeLink>
            )
          }}
          caption={'AZIENDA'}
          visible={clientId === undefined}
          defaultSortOrder="asc"
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 30 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'azienda', 'nome')}>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'sede.nome'}
          caption={'SEDE'}
          allowHeaderFiltering={false}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 27 : undefined}
        />
        <Column
          dataField={'sede.azienda.agente.commerciale_qs.fullname'}
          caption={'AGENTE'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 26 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'agente', 'commerciale_qs.fullname')}>
              <Search enabled={true} searchExpr={'commerciale_qs.fullname'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'sede.azienda.rivenditore.nome'}
          caption={'RIVENDITORE'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 25 : undefined}
          visible={false}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'Azienda', 'rivenditore.nome')}>
              <Search enabled={true} searchExpr={'rivenditore.nome'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'software.nome'}
          caption={'SOFTWARE'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 24 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'software.nome')}>
              <Search enabled={true} searchExpr={'software.nome'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'versione'}
          caption={'VERSIONE'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 23 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'versione')}>
              <Search enabled={true} searchExpr={'versione'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'piano_mantenimento.nome'}
          caption={'PIANO'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 22 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'piano_mantenimento.nome')}>
              <Search enabled={true} searchExpr={'piano_mantenimento.nome'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'stato_noleggio.nome'}
          caption={'NOLEGGIO'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 21 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'stato_noleggio.nome')}>
              <Search enabled={true} searchExpr={'stato_noleggio.nome'} />
            </HeaderFilter>
          )}
        </Column>
        {DateColumn({
          dataField: 'scadenza',
          caption: 'SCADENZA',
          format: 'dd/MM/yyyy',
          hidingPriority: currentScreenSize.isSmall || currentScreenSize.isXSmall ? 20 : undefined,
        })}
        {DateColumn({
          dataField: 'data_vendita',
          caption: 'VENDITA',
          format: 'dd/MM/yyyy',
          hidingPriority: currentScreenSize.isSmall || currentScreenSize.isXSmall ? 19 : undefined,
        })}
        <Column dataField={'altro_fornitore'} caption={'CA'} visible={false} allowHeaderFiltering={false} />
        <Column
          dataField={'seriale'}
          caption={'SERIALE'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 29 : undefined}
          width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 110 : undefined}
          minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 110 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'seriale')}>
              <Search enabled={true} searchExpr={'seriale'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'n_contratto'}
          caption={'N.CONTRATTO'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 28 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'n_contratto')}>
              <Search enabled={true} searchExpr={'n_contratto'} />
            </HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'rete'}
          caption={'NLM'}
          dataType={'boolean'}
          allowHeaderFiltering={false}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 18 : undefined}
        />
        <Column
          dataField={'durata'}
          caption={'DURATA'}
          dataType={'number'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 17 : undefined}
        >
          {!clientId && <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'durata')}></HeaderFilter>}
        </Column>
        <Column
          dataField={'postazioni'}
          caption={'PDL'}
          dataType={'number'}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 16 : undefined}
        >
          {!clientId && (
            <HeaderFilter dataSource={columnSourceFactory(token, 'est_licenza', 'postazioni')}></HeaderFilter>
          )}
        </Column>
        <Column
          dataField={'note'}
          caption={'NOTE'}
          visible={false}
          allowHeaderFiltering={false}
          hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 15 : undefined}
        />
        <Toolbar>
          <Item
            location="before"
            locateInMenu="never"
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Refresh',
              text: 'Refresh',
              icon: 'refresh',
              onClick: (e: ClickEvent) => {
                swAltrePiattaformeGridRef.current?.instance().refresh()
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item name="groupPanel" />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            name="addRowButton"
            options={{
              onClick: () => {
                navigate(`new`)
              },
              text: 'Nuova licenza',
              hint: 'Nuova licenza',
            }}
          />
          <Item
            visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            widget="dxButton"
            options={{
              hint: 'Elimina licenza',
              text: 'Elimina licenza',
              icon: 'trash',
              onClick: () => {
                onDataGridSelectedRowButtonClick(swAltrePiattaformeGridRef, (rowdata: est_licenza) => {
                  console.log(`eliminazione sw ${rowdata['id']} -> ${rowdata['sede']?.azienda?.nome ?? 'Non definito'}`)
                  const result = confirm(
                    `<i>Vuoi davvero eliminare la licenza del cliente <b>${
                      rowdata['sede']?.azienda?.nome ?? 'Non definito'
                    }</b>?</i>`,
                    `Eliminazione licenza`,
                  )
                  result.then((dialogResult) => {
                    if (!dialogResult) return

                    deleteLicenza(rowdata.id)
                      .then((res: any) => {
                        // Aggiornamento griglia
                        swAltrePiattaformeGridRef.current?.instance().refresh()

                        notify(
                          {
                            message: `Licenza '${rowdata['id']}' del cliente ${
                              rowdata['sede']?.azienda?.nome ?? 'Non definito'
                            } eliminata con successo`,
                            type: 'success',
                            displayTime: 5000,
                          },
                          {
                            position: 'bottom center',
                            direction: 'up-push',
                          },
                        )
                      })
                      .catch((error: any) => {
                        console.log('erro', error)
                        notify(
                          {
                            message: `Errore eliminazione licenza: ${error}`,
                            type: 'error',
                            displayTime: 5000,
                          },
                          {
                            position: 'bottom center',
                            direction: 'up-push',
                          },
                        )
                      })
                  })
                })
              },
              stylingMode: 'text',
            }}
          ></Item>
          <Item
            name="columnChooserButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
          />
          <Item
            name="exportButton"
            locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
            showText="inMenu"
            options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
          />
          <Item name="searchPanel" />
        </Toolbar>
      </DataGrid>
      <Popover position="bottom" ref={gridPopoverRef} />
    </>
  )
}

export default SoftwareAltrePiattaformeGrid
